import { useState } from 'react';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useQuery } from '@tanstack/react-query';
import { fetchClaudeUsage, fetchClaudeLimits } from '../api/claude';
import { usePermission } from '../hooks/usePermission';
import { TokenUsageCard } from '../components/claude/TokenUsageCard';
import { SessionUsageTable } from '../components/claude/SessionUsageTable';
import { ClaudeCommandTerminal } from '../components/claude/ClaudeCommandTerminal';

export function Claude() {
  const canView = usePermission('claude.view');
  const [activeWpId, setActiveWpId] = useState<string | null>(null);

  const { data: usage, isLoading: usageLoading, error: usageError } = useQuery({
    queryKey: ['claude', 'usage'],
    queryFn: fetchClaudeUsage,
    refetchInterval: 30_000,
  });

  const { data: limits, isLoading: limitsLoading, error: limitsError } = useQuery({
    queryKey: ['claude', 'limits'],
    queryFn: fetchClaudeLimits,
  });

  if (!canView) {
    return <Alert severity="error">You do not have permission to view the Claude page.</Alert>;
  }

  const error = usageError ?? limitsError;
  const isNotConfigured =
    error instanceof Error &&
    (error as { response?: { status?: number } }).response?.status === 501;

  if (isNotConfigured) {
    return (
      <Alert severity="info">
        MySQL is not configured. Add a <code>mysql:</code> block to{' '}
        <code>/etc/webadmin/app.yaml</code> and restart the service.
      </Alert>
    );
  }

  const isLoading = usageLoading || limitsLoading;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <SmartToyIcon color="primary" />
        <Typography variant="h5">Claude</Typography>
      </Stack>

      {isLoading ? (
        <CircularProgress />
      ) : usage && limits ? (
        <Stack spacing={3}>
          <TokenUsageCard weekly={usage.weekly} limits={limits} />

          <Box>
            <Typography variant="h6" gutterBottom>Sessions</Typography>
            <SessionUsageTable sessions={usage.sessions} />
          </Box>

          <ClaudeCommandTerminal
            activeWpId={activeWpId}
            onWpClaimed={(wpId) => setActiveWpId(wpId)}
            onWpReleased={() => setActiveWpId(null)}
          />
        </Stack>
      ) : error ? (
        <Alert severity="error">Failed to load Claude data.</Alert>
      ) : null}
    </Box>
  );
}
